/**
 * Хранилище курсов на устройстве (ingdg_com#292): IndexedDB.
 *
 * Два хранилища: `sostoyanie` — прогресс одним документом, `zapisi` —
 * записи голоса человека (первая и последняя по каждому примеру, для
 * «дневника голоса»). Записи никуда не отправляются.
 *
 * Ошибки молчат, как в perevodchik-zvuk.ts: в приватном окне или при
 * запрете хранения курс работает, просто ничего не помнит.
 */
import { pustojProgress, type Progress } from './progress';

const BAZA = 'ais-kursy';
const SOSTOYANIE = 'sostoyanie';
const ZAPISI = 'zapisi';

function otkryt(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(BAZA, 1);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains(SOSTOYANIE)) db.createObjectStore(SOSTOYANIE);
      if (!db.objectStoreNames.contains(ZAPISI)) db.createObjectStore(ZAPISI);
    };
    r.onsuccess = () => {
      resolve(r.result);
    };
    r.onerror = () => {
      reject(r.error ?? new Error('IndexedDB недоступна'));
    };
  });
}

async function zapros<T>(
  store: string,
  rezhim: IDBTransactionMode,
  f: (s: IDBObjectStore) => IDBRequest,
): Promise<T | undefined> {
  try {
    const db = await otkryt();
    return await new Promise<T | undefined>((resolve) => {
      const t = db.transaction(store, rezhim);
      const r = f(t.objectStore(store));
      r.onsuccess = () => {
        resolve(r.result as T);
      };
      r.onerror = () => {
        resolve(undefined);
      };
      t.oncomplete = () => {
        db.close();
      };
    });
  } catch {
    return undefined;
  }
}

/** Прогресс курса; у каждого курса свой ключ. */
export async function prochitatProgress(kurs: string): Promise<Progress> {
  const p = await zapros<Progress>(SOSTOYANIE, 'readonly', (s) => s.get(`progress:${kurs}`));
  return p?.v === 1 ? p : pustojProgress();
}

export async function sohranitProgress(kurs: string, p: Progress): Promise<void> {
  await zapros(SOSTOYANIE, 'readwrite', (s) => s.put(p, `progress:${kurs}`));
}

/** Уроки, открытые кнопкой «уже умею». */
export async function prochitatOtkrytye(kurs: string): Promise<string[]> {
  return (await zapros<string[]>(SOSTOYANIE, 'readonly', (s) => s.get(`otkryty:${kurs}`))) ?? [];
}

export async function otkrytUrok(kurs: string, urok: string): Promise<void> {
  const byli = await prochitatOtkrytye(kurs);
  if (!byli.includes(urok))
    await zapros(SOSTOYANIE, 'readwrite', (s) => s.put([...byli, urok], `otkryty:${kurs}`));
}

export interface ZapisGolosa {
  zvuk: Blob;
  kogda: number;
}

/**
 * Запись голоса: первая сохраняется один раз и больше не меняется,
 * последняя — каждый раз. Так через неделю есть с чем сравнить.
 */
export async function sohranitZapis(kluch: string, zvuk: Blob): Promise<void> {
  const z: ZapisGolosa = { zvuk, kogda: Date.now() };
  const pervaya = await zapros<ZapisGolosa>(ZAPISI, 'readonly', (s) => s.get(`${kluch}:pervaya`));
  if (!pervaya) await zapros(ZAPISI, 'readwrite', (s) => s.put(z, `${kluch}:pervaya`));
  await zapros(ZAPISI, 'readwrite', (s) => s.put(z, `${kluch}:poslednyaya`));
}

export async function prochitatZapisi(
  kluch: string,
): Promise<{ pervaya?: ZapisGolosa; poslednyaya?: ZapisGolosa }> {
  const pervaya = await zapros<ZapisGolosa>(ZAPISI, 'readonly', (s) => s.get(`${kluch}:pervaya`));
  const poslednyaya = await zapros<ZapisGolosa>(ZAPISI, 'readonly', (s) =>
    s.get(`${kluch}:poslednyaya`),
  );
  return { pervaya, poslednyaya };
}

/**
 * Просим браузер не стирать данные сайта. Safari без этого чистит их
 * через 7 дней без визитов, а вместе с ними — прогресс и скачанный курс.
 */
export async function poprositHranit(): Promise<boolean> {
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/** Выгрузка прогресса в файл: перенос на другое устройство без регистрации. */
export function vygruzit(kurs: string, p: Progress): Blob {
  return new Blob([JSON.stringify({ kurs, progress: p })], { type: 'application/json' });
}

export function zagruzit(kurs: string, tekst: string): Progress | null {
  try {
    const d = JSON.parse(tekst) as { kurs?: string; progress?: Progress };
    if (d.kurs !== kurs || d.progress?.v !== 1) return null;
    return d.progress;
  } catch {
    return null;
  }
}
